/**
 * From/To guards for date ranges (ENG-28341): the wallet ledger's From/To and
 * the `rangeDate` filters — "PO confirmed date" on Order Details and "Invoice
 * date" on Invoice Details. Values are ISO dates (YYYY-MM-DD), which compare as
 * strings; a cleared date field holds "".
 */

export const INVERTED_RANGE_MESSAGE = "To date can't be before From date";

/**
 * True when both ends are set and To is before From. The same day is valid.
 * Only To needs the emptiness check: no date sorts before an empty or missing
 * From, while an empty To ("") would sort before every date.
 */
export function isInvertedRange(from?: string | null, to?: string | null): boolean {
  return !!to && to < (from ?? "");
}

interface RangeFilterItem {
  type?: string;
  filters?: Array<{ filterKey?: string }>;
}

type FilterValues = Record<string, unknown> | null | undefined;

const valueOf = (values: FilterValues, key?: string): string | undefined => {
  const value = key ? values?.[key] : undefined;
  return typeof value === "string" ? value : undefined;
};

/**
 * The To keys of `rangeDate` filters whose To is before their From, so the
 * filter can't apply. A range filter's `filters` are [From, To].
 */
export function invertedRangeToKeys(
  filterData: Array<RangeFilterItem | null | undefined> | null | undefined,
  values: FilterValues,
): string[] {
  const keys: string[] = [];
  (filterData ?? []).forEach((item) => {
    if (item?.type !== "rangeDate") return;
    const [from, to] = item.filters ?? [];
    if (to?.filterKey && isInvertedRange(valueOf(values, from?.filterKey), valueOf(values, to.filterKey))) {
      keys.push(to.filterKey);
    }
  });
  return keys;
}

/**
 * The picker bound for one side of a range filter: From can't pass To, To
 * can't precede From. Empty while the other side is unset.
 */
export function rangeInputProps(
  item: RangeFilterItem | null | undefined,
  index: number,
  values: FilterValues,
): { min?: string; max?: string } {
  const [from, to] = item?.filters ?? [];
  const other = index === 0 ? valueOf(values, to?.filterKey) : valueOf(values, from?.filterKey);
  if (!other) return {};
  return index === 0 ? { max: other } : { min: other };
}
